import { CircularProgress, MenuItem, Pagination, Select, SelectChangeEvent, Stack, Table, TableBody, TableCell, TableHead, TableRow, Chip, Tooltip, Typography } from "@mui/material";
import React from "react";
import { getUserPurchaseRequests } from "../Api";
import PurchaseRequestDto from "../models/dtos/PurchaseRequestDto";

/** Billing types (betup-server-api UserPurchaseBillingType): what kind of call the request was. */
const BILLING_LABEL: Record<string, string> = {
    REGULAR: 'Shop item',
    OFFER: 'Special offer',
    SUBSCRIPTION: 'Subscription (app)',
    SUBSCRIPTION_NOTIFICATION: 'Subscription (store notification)',
};

interface PurchaseRequestsTableProps {
    userId: number;
}

export default function PurchaseRequestsTable(props: PurchaseRequestsTableProps) {

    const [state, setState] = React.useState({
        items: [] as PurchaseRequestDto[],
        loaded: false,
        page: 1,
        totalPages: 1,
        pageSize: 25
    });

    const fetchData = async (page = 1) => {
        setState(prevState => ({ ...prevState, loaded: false }));
        const offset = (page - 1);
        const limit = state.pageSize;

        const response = await getUserPurchaseRequests(props.userId, offset, limit);
        if (response) {
            const totalPages = Math.max(1, Math.ceil(response.totalItems / state.pageSize));
            setState(prevState => ({
                ...prevState,
                items: response.items ?? [],
                loaded: true,
                totalPages: totalPages,
                page: page
            }));
        } else {
            setState(prevState => ({ ...prevState, loaded: true }));
        }
    }

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        fetchData(page);
    };

    const handlePageSelectChange = (event: SelectChangeEvent<number>) => {
        const selectedPage = Number(event.target.value);
        fetchData(selectedPage);
    };

    React.useEffect(() => {
        fetchData(1);
    }, []);

    const getStatusColor = (status: string | null): "default" | "success" | "error" | "warning" | "info" => {
        switch (status?.toLowerCase()) {
            case 'success':
            case 'completed': return 'success';
            case 'fail':
            case 'failed': return 'error';
            case 'skip': return 'info';
            case 'pending': return 'warning';
            default: return 'default';
        }
    };

    return (
        <>
            {!state.loaded ? (
                <CircularProgress size='100px' style={{ marginBottom: '32px' }} />
            ) : (
                <>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell align="left">ID</TableCell>
                                <TableCell align="left">Product ID</TableCell>
                                <TableCell align="left">Billing Type</TableCell>
                                <TableCell align="left">Store</TableCell>
                                <TableCell align="left">Environment</TableCell>
                                <TableCell align="left">Status</TableCell>
                                <TableCell align="left">Validation</TableCell>
                                <TableCell align="left">Purchased At</TableCell>
                                <TableCell align="left">Reported At</TableCell>
                                <TableCell align="left">Error</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {state.items.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center">No purchase requests found</TableCell>
                                </TableRow>
                            ) : (
                                state.items.map(item => (
                                    <TableRow key={item.id}>
                                        <TableCell>{item.id}</TableCell>
                                        <TableCell>{item.productId || '-'}</TableCell>
                                        <TableCell>
                                            {item.billingType ? (BILLING_LABEL[item.billingType] ?? item.billingType) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {item.store?.toLowerCase().startsWith('google')
                                                ? <img src='/android_logo.png' width={24} height={24} alt={item.store} />
                                                : item.store?.toLowerCase().startsWith('apple')
                                                    ? <img src='/ios_logo.png' width={24} height={24} alt={item.store} />
                                                    : item.store || '-'}
                                        </TableCell>
                                        <TableCell>{item.environment || '-'}</TableCell>
                                        <TableCell>
                                            <Chip label={item.status || 'unknown'} color={getStatusColor(item.status)} size="small" />
                                        </TableCell>
                                        <TableCell>
                                            {item.validationResult ? (
                                                <Chip
                                                    label={item.validationResult.isValid ? 'Valid' : 'Invalid'}
                                                    color={item.validationResult.isValid ? 'success' : 'error'}
                                                    size="small"
                                                />
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {item.validationResult?.purchaseDate ? new Date(item.validationResult.purchaseDate).toLocaleString() : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {item.createdAt ? new Date(item.createdAt).toLocaleString() : '-'}
                                        </TableCell>
                                        <TableCell style={{ maxWidth: 180 }}>
                                            {item.errorMessage ? (
                                                <Tooltip title={item.errorMessage} placement="top">
                                                    <Typography variant="body2" noWrap style={{ maxWidth: 180, cursor: 'default' }}>
                                                        {item.errorMessage}
                                                    </Typography>
                                                </Tooltip>
                                            ) : '-'}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{ mt: 3, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <Pagination
                            count={state.totalPages}
                            page={state.page}
                            onChange={handlePageChange}
                            color="primary"
                            showFirstButton
                            showLastButton
                        />
                        <Select
                            value={state.page}
                            onChange={handlePageSelectChange}
                            label="Navigate to ..."
                            style={{ width: '150px' }}
                        >
                            {Array.from({ length: state.totalPages }, (_, i) => i + 1).map(page => (
                                <MenuItem key={page} value={page}>
                                    Page {page}
                                </MenuItem>
                            ))}
                        </Select>
                    </Stack>
                </>
            )}
        </>
    );
}
